import { Link, router, usePage } from '@inertiajs/react'
import {
  AppShell,
  Burger,
  Group,
  Menu,
  NavLink,
  ScrollArea,
  Select,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import {
  Bookmark,
  BookOpen,
  Box,
  ChevronDown,
  CircleDot,
  Folder,
  FolderOpen,
  Globe,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  Plus,
  User,
  Settings,
  TrendingUp,
  Wand2,
} from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import ActiveSessionsList from '@/components/ActiveSessionsList'
import FlashMessages from '@/components/FlashMessages'
import { submitHiddenForm } from '@/lib/formSubmission'
import type { ProjectTreeNodeProps, SharedProps } from '@/types'

const NEW_PROJECT_VALUE = '__new_project__'

type ProjectItemMeta = { depth: number; projectType: string }

function buildProjectSelectData(projects: ProjectTreeNodeProps[]) {
  const childrenMap = new Map<string | null, ProjectTreeNodeProps[]>()
  let globalProjectId: string | null = null

  for (const project of projects) {
    const key = project.parent_project_id ?? null
    if (!childrenMap.has(key)) childrenMap.set(key, [])
    childrenMap.get(key)!.push(project)
    if (project.project_type === 'global') globalProjectId = project.id
  }

  const items: { value: string; label: string; disabled?: boolean }[] = []
  const metaMap = new Map<string, ProjectItemMeta>()

  function walk(parentId: string | null, depth: number) {
    for (const child of childrenMap.get(parentId) || []) {
      items.push({ value: child.id, label: child.name, disabled: child.project_type === 'folder' })
      metaMap.set(child.id, { depth, projectType: child.project_type })
      walk(child.id, depth + 1)
    }
  }
  walk(null, 0)

  items.push({ value: NEW_PROJECT_VALUE, label: 'New project' })
  metaMap.set(NEW_PROJECT_VALUE, { depth: 0, projectType: 'new' })

  return { items, globalProjectId, metaMap }
}

function useSelectedProject(url: string, projectIds: string[], globalProjectId: string | null) {
  const urlProjectId = url.match(/\/projects\/([^/]+)/)?.[1] ?? null
  const validUrlProject = urlProjectId && projectIds.includes(urlProjectId) ? urlProjectId : null

  const [selectedId, setSelectedId] = useState<string | null>(validUrlProject ?? globalProjectId)

  // Sync when navigating to a project page
  useEffect(() => {
    if (validUrlProject) {
      setSelectedId(validUrlProject)
    }
  }, [validUrlProject])

  // Fall back to global if selection becomes invalid
  useEffect(() => {
    if (selectedId === null || !projectIds.includes(selectedId)) {
      if (globalProjectId) setSelectedId(globalProjectId)
    }
  }, [selectedId, projectIds, globalProjectId])

  return [selectedId, setSelectedId] as const
}

export default function AppLayout({ children, padding = 'md' }: { children: ReactNode; padding?: string | number }) {
  const { current_user, current_organization, projects, flash, csrf_token } = usePage<SharedProps>().props
  const { url } = usePage()
  const [mobileOpened, { toggle: toggleMobile }] = useDisclosure()
  const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(true)

  const { items: projectSelectData, globalProjectId, metaMap } = useMemo(
    () => buildProjectSelectData(projects ?? []),
    [projects],
  )
  const projectIds = projectSelectData.map(p => p.value)
  const [selectedProjectId, setSelectedProjectId] = useSelectedProject(url, projectIds, globalProjectId)

  const orgSlug = current_organization?.slug
  const navItems = [
    { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { label: 'Chats', href: selectedProjectId ? `/projects/${selectedProjectId}/chats` : null, icon: MessageSquare },
    { label: 'Issues', href: selectedProjectId ? `/projects/${selectedProjectId}/issues` : null, icon: CircleDot },
    { label: 'Files', href: selectedProjectId ? `/projects/${selectedProjectId}/files` : null, icon: FolderOpen },
    { label: 'Wiki', href: selectedProjectId ? `/projects/${selectedProjectId}/wiki_pages` : null, icon: BookOpen },
    { label: 'Skills', href: '/skills', icon: Wand2 },
    { label: 'Resources', href: selectedProjectId ? `/projects/${selectedProjectId}/services` : null, icon: Bookmark },
    { label: 'Usage', href: orgSlug ? `/orgs/${orgSlug}/usage` : null, icon: TrendingUp },
    { label: 'Settings', href: orgSlug ? `/orgs/${orgSlug}/settings` : null, icon: Settings },
  ]

  const handleSignOut = () => {
    submitHiddenForm({
      action: '/users/sign_out',
      method: 'delete',
      values: { authenticity_token: csrf_token || '' },
    })
  }

  return (
    <AppShell
      padding={padding}
      header={{ height: 60 }}
      navbar={{
        width: 300,
        breakpoint: 'sm',
        collapsed: { mobile: !mobileOpened, desktop: !desktopOpened },
      }}
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group gap="md">
            <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="sm" size="sm" />
            <Burger opened={desktopOpened} onClick={toggleDesktop} visibleFrom="sm" size="sm" />
            <Text
              component={Link}
              href="/dashboard"
              fw={700}
              size="lg"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              Funuru
            </Text>

            {projectSelectData.length > 0 && (
              <Select
                placeholder="Select project"
                data={projectSelectData}
                value={selectedProjectId}
                onChange={(value) => {
                  if (!value) return
                  if (value === NEW_PROJECT_VALUE) {
                    router.visit(orgSlug ? `/orgs/${orgSlug}/projects` : '/projects')
                    return
                  }
                  setSelectedProjectId(value)
                  const projectUrlMatch = url.match(/^\/projects\/[^/]+(\/.*)?$/)
                  const suffix = projectUrlMatch?.[1] ?? '/chats'
                  router.visit(`/projects/${value}${suffix}`)
                }}
                size="sm"
                w={200}
                visibleFrom="sm"
                allowDeselect={false}
                renderOption={({ option }) => {
                  const meta = metaMap.get(option.value)
                  const depth = meta?.depth ?? 0
                  const type = meta?.projectType ?? 'project'
                  if (type === 'new') {
                    return (
                      <Group gap={6} wrap="nowrap">
                        <Plus size={14} strokeWidth={1.5} style={{ flexShrink: 0 }} />
                        <Text size="sm" c="blue">{option.label}</Text>
                      </Group>
                    )
                  }
                  const Icon = type === 'global' ? Globe : type === 'folder' ? Folder : Box
                  return (
                    <Group gap={6} wrap="nowrap" style={{ paddingLeft: depth * 16 }}>
                      <Icon size={14} strokeWidth={1.5} style={{ opacity: type === 'folder' ? 0.5 : 1, flexShrink: 0 }} />
                      <Text size="sm" c={type === 'folder' ? 'dimmed' : undefined} truncate>
                        {option.label}
                      </Text>
                    </Group>
                  )
                }}
              />
            )}
          </Group>

          <Menu shadow="md" width={200}>
            <Menu.Target>
              <UnstyledButton>
                <Group gap="xs">
                  <User size={18} />
                  <Text size="sm" visibleFrom="sm">{current_user?.email}</Text>
                  <ChevronDown size={14} />
                </Group>
              </UnstyledButton>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Label>{current_user?.email}</Menu.Label>
              <Menu.Divider />
              <Menu.Item
                leftSection={<LogOut size={14} />}
                onClick={handleSignOut}
              >
                Sign out
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar>
        <AppShell.Section grow component={ScrollArea} p="md">
          <Stack gap={4}>
            {navItems.map(item => {
              const disabled = !item.href || item.href === '#'
              return (
                <NavLink
                  key={item.label}
                  component={disabled ? 'a' : Link}
                  href={disabled ? undefined : item.href}
                  label={item.label}
                  leftSection={<item.icon size={18} strokeWidth={1.5} />}
                  active={!disabled && url.startsWith(item.href)}
                  disabled={disabled}
                />
              )
            })}
          </Stack>
        </AppShell.Section>
        <AppShell.Section>
          <ActiveSessionsList />
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        {flash && (flash.notice || flash.alert) && (
          <div style={{ padding: padding === 0 ? '8px 16px 0' : undefined }}>
            <FlashMessages flash={flash} />
          </div>
        )}
        {children}
      </AppShell.Main>
    </AppShell>
  )
}
